import { Component, ElementRef, Input, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { HeatmapService } from '../../../../services/heatmap.service';
import { LayerService } from '../../../../services/layer.service';
import { HeatmapMode, IMapcoreFeatureLayer } from '../../../../types';

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-heatmap-editor-ng',
    templateUrl: './heatmap-editor.component.html',
    styleUrls: ['../../../sharedStyles/base.scss', './heatmap-editor.component.scss']
})
export class HeatmapEditorComponent {
    protected esriLayer:  IMapcoreFeatureLayer;

    protected _heatmapRenderer: __esri.HeatmapRenderer;
    protected _uniqueLayerId: string;
    protected layerService: LayerService;
    protected heatmapService: HeatmapService;
    protected _blurRadius = 0;
    protected _heatmapMode = HeatmapMode.Off;
    private _canvas: ElementRef;

    @ViewChild('canvas') set canvas(canvas: ElementRef) {
        if (canvas) {
            this._canvas = canvas;
            this.updateColorRamp();
        }
    }

    constructor(_vcr: ViewContainerRef, layerService: LayerService, heatmapService: HeatmapService) {
        this.layerService = layerService;
        this.heatmapService = heatmapService;
    }

    @Input() set uniqueLayerId(value: string) {
        this._uniqueLayerId = value;
        if (value) {
            this.esriLayer = this.layerService.getEsriLayerByUniqueId(this.uniqueLayerId) as IMapcoreFeatureLayer;
        }
    }
    get uniqueLayerId(): string {
        return this._uniqueLayerId;
    }

    @Input() set heatmapRenderer(value: NonNullable<__esri.HeatmapRenderer>) {
        this._heatmapRenderer = value;
        this.bindForm();
    }
    get heatmapRenderer(): __esri.HeatmapRenderer {
        return this._heatmapRenderer;
    }

    set blurRadius(value: number) {
        this._blurRadius = value;
        if (value && this.esriLayer) {
            this.heatmapRenderer.blurRadius = this.blurRadius;
        }
    }
    get blurRadius(): number {
        return this._blurRadius;
    }

    @Input() set heatmapMode(value: HeatmapMode) {
        this._heatmapMode = value;
    }
    get heatmapMode() {
        return this._heatmapMode;
    }

    startColorRamp: string;
    endColorRamp: string;
    colorProperties: string[] = ['symbolColor, outlineColor'];

    bindForm() {
        if (this.heatmapRenderer) {
            if (this.heatmapRenderer.type === 'heatmap') {
                const renderer = this.heatmapRenderer;
                this.blurRadius = this.heatmapRenderer.blurRadius;
                this.startColorRamp = `rgb(${renderer.colorStops.map(x => x.color)[1].toRgb().join(', ')})`;
                this.endColorRamp = `rgb(${renderer.colorStops.map(x => x.color)[2].toRgb().join(', ')})`;
                this.updateColorRamp();
            }
        }
    }

    async updateColorFromPicker(evt, colorProperty) {
        this[colorProperty] = evt;
        if (this.esriLayer && evt) {
            const rgbColor = await this.heatmapService.getColorString(evt, false);
            const initColor = await this.heatmapService.getColorString(evt, true, true);
            const rgbaColor = await this.heatmapService.getColorString(evt, true);
            this.heatmapRenderer.colorStops[colorProperty].color.setColor(rgbaColor);
            if (colorProperty === '1') {
                this.startColorRamp = rgbColor;
                this.heatmapRenderer.colorStops[0].color.setColor(initColor);
            } else {
                this.endColorRamp = rgbColor;
            }
            this.updateColorRamp();

        }
    }

    updateColorRamp() {
        if (this._canvas) {
            this._canvas.nativeElement.style.background = `linear-gradient(to right, ${this.startColorRamp} 0%, ${this.endColorRamp} 100%)`;
        }
    }

    heatmapToggle(heatmapMode: HeatmapMode) {
        this.esriLayer.mapcoreProps.heatmapMode = heatmapMode;

    }

    async onHeatmapModeChange() {
        if (this.esriLayer) {
            this.esriLayer.mapcoreProps.heatmapMode = this.heatmapMode;
            this.esriLayer = await this.heatmapService.updateHeatmapLayers(this.esriLayer);
        }
    }

    @Input()
    public applyChange = () => {
        return this.heatmapRenderer;
    }

    onPixelIntensityChange(e) {
        if (e.detail) {
            this.heatmapRenderer.minPixelIntensity = e.detail.minValue;
            this.heatmapRenderer.maxPixelIntensity = e.detail.maxValue;
        }
    }
}
